import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';

const TIMEZONES = [
  'GMT (UTC+0)', 'CET (UTC+1)', 'EET (UTC+2)', 'EAT (UTC+3)',
  'GST (UTC+4)', 'IST (UTC+5:30)', 'SGT (UTC+8)', 'EST (UTC-5)',
  'CST (UTC-6)', 'PST (UTC-8)', 'Other'
];

const INVESTOR_TYPES = [
  { key: 'uk_investor', label: 'UK-based Investor' },
  { key: 'us_investor', label: 'US-based Investor' },
  { key: 'uae_investor', label: 'UAE/Gulf-based Investor' },
  { key: 'global_investor', label: 'Global Investor' },
  { key: 'partner_bank', label: 'Partner bank' },
];

const INTEREST_OPTIONS = [
  { key: 'T-bills', label: '91-day T-bill auction participation' },
  { key: 'FX-forwards', label: 'Exchange rate forwards protection & pooling' },
  { key: 'Depository', label: 'Depository account opening process' },
  { key: 'Rollover', label: 'Automated 91-day rollover' },
  { key: 'Dashboard', label: 'Portfolio dashboard walkthrough' },
  { key: 'Bank', label: 'Bank distribution platform (for banks)' },
];

// Two-tone text formatting component — no bold, lightweight
const ForwardsflowText = ({ variant = 'default' }) => (
  <span className="font-sans font-normal inline-block">
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

const formatBrandText = (text, variant = 'default') => {
  if (typeof text !== 'string') return text;
  const tokens = [];
  let currentIdx = 0;
  const regex = /(Forwardsflow)/gi;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIdx) {
      tokens.push(text.substring(currentIdx, match.index));
    }
    tokens.push(<ForwardsflowText key={match.index} variant={variant} />);
    currentIdx = regex.lastIndex;
  }

  if (currentIdx < text.length) {
    tokens.push(text.substring(currentIdx));
  }
  return tokens.length > 0 ? tokens : text;
};

const BookDemoPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    investorType: '',
    organizationName: '',
    country: '',
    contactName: '',
    email: '',
    phone: '',
    interests: [],
    preferredDate: '',
    preferredTime: '',
    timezone: '',
    estimatedAllocation: '',
    additionalInfo: ''
  });

  const handleInterestToggle = (key) => {
    if (formData.interests.includes(key)) {
      setFormData({
        ...formData,
        interests: formData.interests.filter(i => i !== key)
      });
    } else {
      setFormData({
        ...formData,
        interests: [...formData.interests, key]
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Home</Link>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold border-b-2 border-black pb-1">Book a Demo</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>
              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          {submitted ? (
            <div className="border border-zinc-300 p-10 text-center bg-white mt-12">
              <div className="w-12 h-12 border border-zinc-300 flex items-center justify-center mx-auto mb-6 bg-zinc-50">
                <CheckCircle className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-xl font-bold uppercase tracking-wider text-black mb-3">Demo Scheduled</h3>
              <p className="text-xs font-mono text-zinc-600 leading-relaxed mb-6">
                Your demo has been scheduled. A meeting invite and access link have been sent to your email. We look forward to walking you through the platform.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium hover:bg-black transition-all"
              >
                Return Home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div>
              <div className="text-center mb-10">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-2">Platform Demonstration</span>
                <h1 className="text-3xl font-bold font-sans text-black">{formatBrandText("Book a Forwardsflow demo")}</h1>
                <p className="text-xs font-mono text-zinc-500 mt-2">Schedule a live walkthrough of frontier fixed income investment with exchange rate forwards protection, depository settlement, and automated rollover.</p>
              </div>

              <div className="border border-zinc-300 p-8 bg-white">
                <form onSubmit={handleSubmit} className="space-y-6 font-mono text-xs text-black">
                  {/* Investor Type */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Investor Type *</label>
                    <select
                      required
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                      value={formData.investorType}
                      onChange={(e) => setFormData({ ...formData, investorType: e.target.value })}
                    >
                      <option value="">Select investor type</option>
                      {INVESTOR_TYPES.map((t) => (
                        <option key={t.key} value={t.key}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Organization & Country */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Organization Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Acme Capital Partners"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.organizationName}
                        onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Country *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. United Kingdom"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Personal details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Contact Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="First & Last Name"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.contactName}
                        onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        placeholder="e.g. +44 20 7946 0958"
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Business Email *</label>
                    <input
                      type="email"
                      required
                      placeholder="name@yourfirm.com"
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>

                  {/* Estimated Allocation */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Estimated Initial Allocation (USD)</label>
                    <select
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                      value={formData.estimatedAllocation}
                      onChange={(e) => setFormData({ ...formData, estimatedAllocation: e.target.value })}
                    >
                      <option value="">Select range</option>
                      <option value="10k-100k">$10,000 – $100,000</option>
                      <option value="100k-500k">$100,000 – $500,000</option>
                      <option value="500k-1m">$500,000 – $1,000,000</option>
                      <option value="1m-5m">$1,000,000 – $5,000,000</option>
                      <option value="5m+">$5,000,000+</option>
                    </select>
                  </div>

                  {/* Interests Checklist */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-3">Areas of Demonstration Interest</label>
                    <div className="space-y-2 font-sans text-xs">
                      {INTEREST_OPTIONS.map((opt) => (
                        <div key={opt.key} className="flex items-start gap-3 py-1">
                          <input
                            type="checkbox"
                            id={`interest-${opt.key}`}
                            className="w-4 h-4 mt-0.5 border-zinc-300 text-black focus:ring-0 rounded-none"
                            checked={formData.interests.includes(opt.key)}
                            onChange={() => handleInterestToggle(opt.key)}
                          />
                          <label htmlFor={`interest-${opt.key}`} className="text-zinc-600 cursor-pointer">
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Schedule details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Preferred Date *</label>
                      <input
                        type="date"
                        required
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans bg-transparent"
                        value={formData.preferredDate}
                        onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wider text-zinc-500 mb-2">Preferred Time *</label>
                      <input
                        type="time"
                        required
                        className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans bg-transparent"
                        value={formData.preferredTime}
                        onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Timezone */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Time Zone *</label>
                    <select
                      required
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs bg-transparent focus:outline-none focus:border-black font-sans"
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    >
                      <option value="">Select Time Zone</option>
                      {TIMEZONES.map((zone) => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                  </div>

                  {/* Additional Info */}
                  <div>
                    <label className="block uppercase tracking-wider text-zinc-500 mb-2">Additional Information</label>
                    <textarea
                      rows="3"
                      placeholder="Specific markets of interest, questions, or requirements..."
                      className="w-full border border-zinc-300 rounded-none px-4 py-3 text-xs focus:outline-none focus:border-black font-sans"
                      value={formData.additionalInfo}
                      onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full py-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                  >
                    Schedule Demo Call
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookDemoPage;
